import * as React from 'react';

import { ComponentType } from 'shared/entities/components/Component';
import Stub from 'shared/newComponents/Stub';
import LoaderSpinner from 'shared/newComponents/animations/LoaderSpinner';
import {
  LoaderSpinnerColor,
  LoaderSpinnerSize
} from 'shared/newEntities/components/LoaderSpinner';
import { useTranslationsStore } from 'stores';

import { ErrorBoundaryData } from '../ErrorBoundary';
import SentryErrorBoundary from '../SentryErrorBoundary';

const isChunkLoadError = (error?: Error): boolean => {
  if (!error) {
    return false;
  }

  return (
    error.name === 'ChunkLoadError' ||
    (error.message?.includes('Loading chunk') &&
      error.message?.includes('failed')) ||
    (error.message?.includes('Loading CSS chunk') &&
      error.message?.includes('failed'))
  );
};

const ErrorFallback: React.FC<ErrorBoundaryData> = ({ error }) => {
  const { localization } = useTranslationsStore();

  const handleReload = React.useCallback(() => {
    window.location.reload();
  }, []);

  if (isChunkLoadError(error)) {
    return (
      <Stub
        isError
        title={localization.t('LazySuspense.error', { ns: 'components' })}
        button={{
          type: ComponentType.button,
          onClick: handleReload,
          children: localization.t('LazySuspense.reload', {
            ns: 'components'
          })
        }}
      />
    );
  }

  return (
    <Stub
      isError
      title={localization.t('LazySuspense.genericError', {
        ns: 'components'
      })}
    />
  );
};

type Props = {
  children: React.ReactNode;
  fallback?: React.ReactNode;
};

const LazySuspense: React.FC<Props> = ({ children, fallback }) => (
  <SentryErrorBoundary ErrorComponent={ErrorFallback}>
    <React.Suspense
      fallback={
        fallback ?? (
          <LoaderSpinner
            color={LoaderSpinnerColor.blue}
            centered
            size={LoaderSpinnerSize.md}
          />
        )
      }
    >
      {children}
    </React.Suspense>
  </SentryErrorBoundary>
);

export default LazySuspense;
